import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups, TableDropdown } from 'components/molecules';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import DownloadFilesInPeriodPopup from 'components/molecules/popup/download-files-in-period/DownloadFilesInPeriodPopup';
import { FooterTable, QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiDownload, PiLockKey, PiNavigationArrow, PiTrash } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useDeleteSettlement,
  useGetSettlementFile,
  useGetSettlementFilePreview,
  useGetSettlementFiles,
  useGetSettlementInfinite,
  useGetSettlementStatistics,
  usePasswordProtectSettlement,
} from 'services/settlement';
import { ISettlementResponse } from 'services/settlement/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { EMAIL_SEND } from 'services/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  label,
  settlementNumber,
  net,
}

interface ISettlementSearch {
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
}

const settlementSearchInitialValues: ISettlementSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};

const Settlement = () => {
  usePermissionGuard('SettlementPermissionsLIST**', '/', true, 'statements');

  const { t } = useTranslation();
  const i18nextPrefix = 'settlement';

  const columnsVisibility = useTableColumnsVisibility(
    'SettlementListEmail',
    SORT_COLUMNS
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] || settlementSearchInitialValues;

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const settlementPeriods = useGetSettlementLightList();

  const settlements = useGetSettlementInfinite(
    {},
    filters.startSettlementPeriodId || undefined,
    filters.endSettlementPeriodId || undefined
  );
  const { mutateAsync: deleteSettlement } = useDeleteSettlement();
  const passwordProtectSettlement = usePasswordProtectSettlement();
  const { applicationSettings } = useStoreState();
  const isPdfPasswordProtectionEnabled =
    applicationSettings?.enablePdfPasswordProtection ?? false;

  const { setFile } = useStreamFileDownload(useGetSettlementFile);

  const tableProps = useInfiniteTable(settlements, { selectable: false });

  const statistics = useGetSettlementStatistics(
    filters.startSettlementPeriodId || undefined,
    filters.endSettlementPeriodId || undefined
  );

  const netAmountLength = formatCurrency(statistics?.data?.allNetAmount).length;

  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  const handleDelete = async (
    settlementId: string,
    actions: PopupActions | null
  ) => {
    await deleteSettlement(
      { settlementId },
      {
        onSuccess: () => {
          settlements.refetch();
          actions?.close();
        },
      }
    );
  };

  const handlePasswordProtect = async (settlementId: string) => {
    await passwordProtectSettlement.mutateAsync(
      { settlementId },
      {
        onSuccess: () => {
          settlements.refetch();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const goToLabel = (id: string) =>
    window.open(`/label/${id}/settlements`, '_blank');

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex gap-2'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  filters={filters}
                  isClear
                />
                <DownloadFilesInPeriodPopup
                  service={useGetSettlementFiles}
                  filePrefix={t('navbar.settlement')}
                />
                <Table.CustomColumnsButton
                  columns={SORT_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
              </div>
            </>
          }>
          <QueryFilter<ISettlementSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<ISettlementSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startSettlementPeriod')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data => {
                  const period = settlementPeriods.data
                    ?.flat()
                    .find(p => p.id === data.value);
                  return period
                    ? getSettlementPeriodName(period.settlementPeriodName)
                    : `${data.value}`;
                }}
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <FooterTable
        {...tableProps}
        id={columnsVisibility.tableId}
        columns={[
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
            footer: t('table.total'),
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          {
            label: t('input.label.settlementNumber'),
            renderCell: ({ settlementNumber }) => (
              <TableItem>
                <TableItem.Text text={settlementNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.settlementNumber),
          },
          {
            label: t('input.label.net'),
            renderCell: (item: ISettlementResponse) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.netAmount, {
                    length: netAmountLength,
                  })}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.net),
            footer: formatCurrency(statistics.data?.allNetAmount),
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.settlement}
                      refetch={settlements.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: (item: ISettlementResponse) => {
              const showPasswordProtect =
                isPdfPasswordProtectionEnabled &&
                !!item.filePath &&
                !item.isPasswordProtected;
              const isProtectLoading =
                passwordProtectSettlement.isPending &&
                passwordProtectSettlement.variables?.settlementId === item.id;

              return (
                <TableItem justifyPosition='end'>
                  <TableDropdown
                    justifyPosition='end'
                    isLoading={isProtectLoading}
                    style={{ padding: 0 }}>
                    {showPasswordProtect && (
                      <Button
                        type='button'
                        isClear
                        fullWidth
                        size='compact'
                        isLoading={isProtectLoading}
                        onClick={() => handlePasswordProtect(item.id)}
                        buttonStyle={{ justifyContent: 'start' }}>
                        <Button.Icon icon={<PiLockKey />} />
                        <Button.Text text={t('button.password-protect')} />
                      </Button>
                    )}
                    <MagellanPopups.FilePreviewPopup
                      fileId={item.id}
                      fileName={item.settlementNumber ?? ''}
                      fileService={useGetSettlementFilePreview}
                      isPasswordProtected={item.isPasswordProtected}
                      trigger={
                        <Button
                          type='button'
                          isClear
                          fullWidth
                          size='compact'
                          disabled={!item.filePath}
                          buttonStyle={{ justifyContent: 'start' }}>
                          <Button.Icon icon={<PiDownload />} />
                          <Button.Text text={t('button.download')} />
                        </Button>
                      }
                      onDownload={() =>
                        setFile({ id: item.id, name: item.settlementNumber })
                      }
                    />
                    <MagellanPopups.DeleteWarning
                      name={`${t('settlement.what')} ${item.settlementNumber} ${
                        item.labelName ?? ''
                      } `}
                      onRemove={actions => handleDelete(item.id, actions)}
                      trigger={
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          isClear
                          fullWidth
                          size='compact'
                          buttonStyle={{ justifyContent: 'start' }}>
                          <Button.Icon icon={<PiTrash />} />
                          <Button.Text text={t('button.remove')} />
                        </Button>
                      }
                    />
                    <Button
                      type='button'
                      isClear
                      fullWidth
                      size='compact'
                      disabled={!item.labelId}
                      onClick={() => goToLabel(item.labelId)}
                      buttonStyle={{ justifyContent: 'start' }}>
                      <Button.Icon
                        icon={<PiNavigationArrow transform='scale(-1,1)' />}
                      />
                      <Button.Text text={t('button.open')} />
                    </Button>
                  </TableDropdown>
                </TableItem>
              );
            },
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Settlement;
